import React from 'react';
import './styles.module.scss';

/**
 * TranscriptDisplay - Read-only display of transcript segments
 * Shows speakers and their text in a clean, formatted way
 */
export default function TranscriptDisplay({ segments, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading transcript...</span>
      </div>
    );
  }

  if (!segments || segments.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-2">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-gray-500 text-sm">No transcript available yet</p>
      </div>
    );
  }

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getSpeakerColor = (speaker) => {
    // Generate a consistent color based on speaker name
    const colors = [
      'bg-blue-100 text-blue-800 border-blue-200',
      'bg-green-100 text-green-800 border-green-200',
      'bg-purple-100 text-purple-800 border-purple-200',
      'bg-orange-100 text-orange-800 border-orange-200',
      'bg-pink-100 text-pink-800 border-pink-200',
      'bg-indigo-100 text-indigo-800 border-indigo-200',
      'bg-teal-100 text-teal-800 border-teal-200',
      'bg-red-100 text-red-800 border-red-200',
    ];
    
    let hash = 0;
    for (let i = 0; i < speaker.length; i++) {
      hash = speaker.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  return (
    <div className="space-y-1">
      {segments.map((segment, index) => {
        const speaker = segment.speaker || 'UNKNOWN';
        const text = segment.text || '';
        const startTime = segment.start || segment.start_time || 0;
        const colorClass = getSpeakerColor(speaker);
        
        return (
          <div
            key={segment.id || `segment-${index}`}
            className="group flex gap-3 py-3 px-4 hover:bg-gray-50 rounded-lg transition-colors"
          >
            {/* Timestamp */}
            <div className="flex-shrink-0 w-16 pt-0.5">
              <span className="text-xs font-mono text-gray-500">
                {formatTime(startTime)}
              </span>
            </div>
            
            {/* Speaker */}
            <div className="flex-shrink-0 w-32 pt-0.5">
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${colorClass}`}>
                {speaker}
              </span>
            </div>
            
            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
                {text}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

